import { useL10n } from '@uplift/hooks';
import { StepContainer, StepContent, StepForm, StepHeading, StepNavigator, StepTipPopover, StepToggleButton, StepToggleButtonGroup } from '../../components';
import { AssessmentQuestionNextData, AssessmentYesNoQuestionNextData } from '../types';
import { YesNo } from '@uplift/types';
import { Typography } from '@mui/material';

import { makeStyles } from 'tss-react/mui';

type AssessmentQuestionProps = {
  onNext: (data: AssessmentQuestionNextData) => void;
  onBack: () => void;
};

type AssessmentQuestionConstructorProps = {
  heading: string;
} & AssessmentQuestionProps;

const AssessmentQuestionConstructor: React.VFC<AssessmentQuestionConstructorProps> = props => {
  const { classes } = useQuestionStyles();
  const l10n = useL10n();

  return (
    <StepContainer>
      <StepContent>
        <Typography color="textSecondary" className={classes.preheading}>In the past 2 weeks</Typography>
        <StepHeading>{props.heading}</StepHeading>
        <StepForm>
          <StepToggleButtonGroup onChange={(_, score: string) => props.onNext({ score: Number(score) })}>
            <StepToggleButton value="0">{l10n.clientProfile.assessments.scores.phq9.answers.notAtAll}</StepToggleButton>
            <StepToggleButton value="1">{l10n.clientProfile.assessments.scores.phq9.answers.severalDays}</StepToggleButton>
            <StepToggleButton value="2">{l10n.clientProfile.assessments.scores.phq9.answers.moreThanHalf}</StepToggleButton>
            <StepToggleButton value="3">{l10n.clientProfile.assessments.scores.phq9.answers.nearlyEvery}</StepToggleButton>
          </StepToggleButtonGroup>
        </StepForm>
      </StepContent>
      <StepNavigator onBack={props.onBack} desktopOnly />
    </StepContainer>
  );
};

/** PHQ2 */
export const AssessmentQuestion1: React.VFC<AssessmentQuestionProps> = props => {
  return (
    <AssessmentQuestionConstructor
      heading="Have you had little interest or pleasure doing things?"
      {...props}
    />
  );
};

/** PHQ2 */
export const AssessmentQuestion2: React.VFC<AssessmentQuestionProps> = props => {
  return (
    <AssessmentQuestionConstructor
      heading="Have you felt down, depressed, or hopeless?"
      {...props}
    />
  );
};

/** GAD2 */
export const AssessmentQuestion3: React.VFC<AssessmentQuestionProps> = props => {
  return (
    <AssessmentQuestionConstructor
      heading="Have you felt nervous, anxious, or on edge?"
      {...props}
    />
  );
};

/** GAD2 */
export const AssessmentQuestion4: React.VFC<AssessmentQuestionProps> = props => {
  return (
    <AssessmentQuestionConstructor
      heading="Have you been unable to stop or control worrying?"
      {...props}
    />
  );
};

type AssessmentYesNoQuestionProps = {
  onNext: (data: AssessmentYesNoQuestionNextData) => void;
  onBack: () => void;
};

type AssessmentYesNoContructorProps = {
  heading: string;
  includePreferNotToSay?: boolean;
  stepTip?: React.ReactNode;
} & AssessmentYesNoQuestionProps;

const AssessmentYesNoContructor: React.VFC<AssessmentYesNoContructorProps> = props => {
  return (
    <StepContainer>
      <StepContent>
        <Typography />
        <StepHeading>{props.heading}</StepHeading>
        <StepForm>
          <StepToggleButtonGroup onChange={(_, value: YesNo | 'preferNotToSay') => props.onNext({ answer: value === 'preferNotToSay' ? null : value === YesNo.Yes })}>
            <StepToggleButton value={YesNo.Yes}>Yes</StepToggleButton>
            <StepToggleButton value={YesNo.No}>No</StepToggleButton>
            {props.includePreferNotToSay && <StepToggleButton value="preferNotToSay">Prefer not to say</StepToggleButton>}
          </StepToggleButtonGroup>
        </StepForm>
      </StepContent>
      {props.stepTip}
      <StepNavigator onBack={props.onBack} desktopOnly />
    </StepContainer>
  );
};


export const AssessmentMedHistoryQuestion: React.VFC<AssessmentYesNoQuestionProps> = props => {
  return (
    <AssessmentYesNoContructor
      heading="Are you taking or have you ever taken medication to support your mental health?"
      {...props}
    />
  );
};

export const AssessmentHasTherapistQuestion: React.VFC<AssessmentYesNoQuestionProps> = props => {
  return (
    <AssessmentYesNoContructor
      heading="Are you currently seeing a therapist?"
      includePreferNotToSay
      {...props}
    />
  );
};

export const AssessmentIsTherapistGoodFitQuestion: React.VFC<AssessmentYesNoQuestionProps> = props => {
  const { classes } = useQuestionStyles();

  return (
    <AssessmentYesNoContructor
      heading="Do you feel like your therapist is a good fit for you?"
      stepTip={(
        <StepTipPopover title="What’s considered a good fit?">
          <Typography>A good fit would be a therapist who:</Typography>
          <ul className={classes.list}>
            <Typography component="li">You feel comfortable being open and honest with</Typography>
            <Typography component="li">Understands you</Typography>
            <Typography component="li">Helps you work towards your goals</Typography>
            <Typography component="li">Makes you feel accepted and respected</Typography>
          </ul>
        </StepTipPopover>
      )}
      {...props}
    />
  );
};

const useQuestionStyles = makeStyles({ name: 'Question' })(theme => ({
  list: {
    paddingInlineStart: theme.spacing(3),
    margin: 0,
    marginTop: theme.spacing(2)
  },
  preheading: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  }
}));
